import React, { useMemo, useState } from "react";
import type { Theme } from "../lib/theme";
import type { GcStaffRow } from "../lib/generalConditions";
import {
  defaultStaff, blankRow, monthlyCost, rowTotal, gcTotal, toLineItems, staleRows, GC_SUB_NAME,
} from "../lib/generalConditions";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

/**
 * Division 01, as staff over time.
 *
 * Deliberately one screen with one table and one button. The arithmetic is
 * shown on every row -- rate, months, allocation, and what they come to -- so
 * the total is checkable before anything is written, and the lines it writes
 * are ordinary line items an estimator can edit afterwards without coming
 * back here.
 */
export default function StaffingPlanModal({
  T, fmt, duration, laborRates, existing, onApply, onClose,
}: {
  T: Theme;
  fmt: (n: number) => string;
  duration: number;
  laborRates: Array<{ role: string; rate: number }>;
  /** The plan last written, if there is one. */
  existing: GcStaffRow[] | null;
  onApply: (rows: GcStaffRow[]) => void;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<GcStaffRow[]>(
    () => (existing && existing.length ? existing : defaultStaff(duration, laborRates)));

  const total = useMemo(() => gcTotal(rows), [rows]);
  const lines = useMemo(() => toLineItems(rows), [rows]);
  // Rows the schedule has moved away from. Offered as one action rather than
  // applied on open: a role deliberately running short of the schedule is a
  // real staffing decision, and silently "correcting" it would be worse than
  // leaving it alone. Saying nothing would be worse still.
  const stale = useMemo(() => staleRows(rows, duration), [rows, duration]);

  const patch = (id: string, p: Partial<GcStaffRow>) =>
    setRows(rs => rs.map(r => (r.id === id ? { ...r, ...p } : r)));

  const label: React.CSSProperties = {
    fontSize: 10, color: T.textWeak, letterSpacing: ".1em",
    textTransform: "uppercase", fontWeight: 500, textAlign: "left", padding: "0 8px 7px",
  };
  const cell: React.CSSProperties = {
    background: T.bgInput, border: `1px solid ${T.borderSecondary}`, color: T.textSecondary,
    borderRadius: 3, padding: "5px 8px", fontSize: 12, fontFamily: FONT, width: "100%",
    boxSizing: "border-box", outline: "none", minHeight: 24,
  };

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 200,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: 10,
          width: "100%", maxWidth: 780, maxHeight: "86vh", display: "flex", flexDirection: "column",
          fontFamily: FONT, overflow: "hidden" }}>

        <div style={{ padding: "18px 24px 14px", borderBottom: `1px solid ${T.borderPrimary}` }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary }}>Staffing plan</div>
          <div style={{ fontSize: 11, color: T.textWeak, marginTop: 5, lineHeight: 1.6 }}>
            General conditions priced as people over time, at your org's labor rates. The project
            duration is <strong style={{ color: T.textMuted }}>{duration || 0} months</strong>; each
            row carries its own, because a superintendent is usually on for all of it and a project
            manager usually is not.
          </div>
        </div>

        <div style={{ padding: "14px 24px", overflowY: "auto", flex: 1 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...label, width: "34%" }}>Role</th>
                <th style={{ ...label, width: 92 }}>$ / hr</th>
                <th style={{ ...label, width: 78 }}>Months</th>
                <th style={{ ...label, width: 72 }}>% time</th>
                <th style={{ ...label, textAlign: "right" }}>Per month</th>
                <th style={{ ...label, textAlign: "right" }}>Total</th>
                <th style={{ ...label, width: 26 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={{ padding: "0 8px 7px" }}>
                    <input list="oe-gc-roles" value={r.role} placeholder="Role"
                      onChange={e => {
                        const match = laborRates.find(x => x.role === e.target.value);
                        // Picking a known role brings its rate; typing a new
                        // one leaves whatever rate is there to be filled in.
                        patch(r.id, match ? { role: match.role, hourlyRate: match.rate } : { role: e.target.value });
                      }}
                      style={cell} />
                  </td>
                  <td style={{ padding: "0 8px 7px" }}>
                    <input type="number" min="0" step="5" value={r.hourlyRate}
                      onChange={e => patch(r.id, { hourlyRate: +e.target.value })} style={cell} />
                  </td>
                  <td style={{ padding: "0 8px 7px" }}>
                    <input type="number" min="0" step="1" value={r.months}
                      onChange={e => patch(r.id, { months: +e.target.value })}
                      style={{ ...cell, ...(duration > 0 && Math.abs(r.months - duration) > 0.01
                        ? { borderColor: `${T.costStatus["Needs Review"].badgeText}88` } : {}) }} />
                  </td>
                  <td style={{ padding: "0 8px 7px" }}>
                    <input type="number" min="0" max="100" step="5" value={r.allocation}
                      onChange={e => patch(r.id, { allocation: +e.target.value })} style={cell} />
                  </td>
                  <td style={{ padding: "0 8px 7px", textAlign: "right", fontSize: 12,
                    color: T.textMuted, fontVariantNumeric: "tabular-nums" }}>
                    {fmt(monthlyCost(r))}
                  </td>
                  <td style={{ padding: "0 8px 7px", textAlign: "right", fontSize: 12, fontWeight: 600,
                    color: T.textSecondary, fontVariantNumeric: "tabular-nums" }}>
                    {fmt(rowTotal(r))}
                  </td>
                  <td style={{ padding: "0 0 7px", textAlign: "right" }}>
                    <button onClick={() => setRows(rs => rs.filter(x => x.id !== r.id))}
                      title="Remove this role"
                      style={{ background: "none", border: "none", color: T.textWeak, cursor: "pointer",
                        fontSize: 14, padding: "2px 4px", fontFamily: FONT }}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <datalist id="oe-gc-roles">
            {laborRates.map(r => <option key={r.role} value={r.role} />)}
          </datalist>

          {rows.length === 0 && (
            <div style={{ padding: "18px 8px", fontSize: 11, color: T.textWeak }}>
              No roles yet. Add one, or close without writing anything.
            </div>
          )}

          {stale.length > 0 && duration > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "10px 8px 2px",
              padding: "8px 12px", borderRadius: 3,
              background: `${T.costStatus["Needs Review"].badgeText}14`,
              border: `1px solid ${T.costStatus["Needs Review"].badgeText}44` }}>
              <span style={{ fontSize: 11, color: T.costStatus["Needs Review"].badgeText, flex: 1 }}>
                {stale.length} role{stale.length === 1 ? "" : "s"} not on the project duration of {duration} months.
              </span>
              <button onClick={() => setRows(rs => rs.map(r => ({ ...r, months: duration })))}
                style={{ fontSize: 10, padding: "4px 10px", minHeight: 24, borderRadius: 3, cursor: "pointer",
                  fontFamily: FONT, fontWeight: 600, border: `1px solid ${T.costStatus["Needs Review"].badgeText}66`,
                  background: "transparent", color: T.costStatus["Needs Review"].badgeText, whiteSpace: "nowrap" }}>
                Set all to {duration}
              </button>
            </div>
          )}

          <button onClick={() => setRows(rs => [...rs, blankRow(duration)])}
            style={{ fontSize: 11, marginTop: 4, padding: "5px 12px", minHeight: 24, borderRadius: 3,
              border: `1px dashed ${T.borderSecondary}`, background: "transparent",
              color: T.textWeak, cursor: "pointer", fontFamily: FONT }}>
            + Add role
          </button>
        </div>

        <div style={{ padding: "14px 24px 18px", borderTop: `1px solid ${T.borderPrimary}` }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: T.textWeak }}>
              {lines.length} line{lines.length === 1 ? "" : "s"} into Division 01 ·{" "}
              <span style={{ color: T.textMuted }}>{GC_SUB_NAME}</span>
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.borderAccent, fontVariantNumeric: "tabular-nums" }}>
              {fmt(total)}
            </div>
          </div>
          {/* Said before the button, not after: re-running is how you update
              a plan when the schedule moves, and it has to be obvious that it
              replaces rather than appends. Only this one subcategory. */}
          <div style={{ fontSize: 10, color: T.textWeak, marginBottom: 12, lineHeight: 1.6 }}>
            Writes ordinary line items — months as the quantity, the monthly cost as the unit cost — which you
            can edit afterwards like any other. Running this again replaces everything in {GC_SUB_NAME} and
            touches nothing else in Division 01.
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={onClose}
              style={{ fontSize: 11, padding: "6px 14px", minHeight: 24, borderRadius: 3, cursor: "pointer",
                border: `1px solid ${T.borderSecondary}`, background: "transparent",
                color: T.textWeak, fontFamily: FONT }}>Cancel</button>
            <button onClick={() => onApply(rows)} disabled={lines.length === 0}
              style={{ fontSize: 11, padding: "6px 16px", minHeight: 24, borderRadius: 3, fontWeight: 600,
                cursor: lines.length === 0 ? "default" : "pointer", fontFamily: FONT,
                border: `1px solid ${T.borderAccent}`, background: T.bgAccentLight,
                color: T.borderAccent, opacity: lines.length === 0 ? .45 : 1 }}>
              {existing && existing.length ? "Update Division 01" : "Write to Division 01"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
